import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Divider, CircularProgress,
  Checkbox, FormControlLabel
} from '@mui/material';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import AssignmentIcon from '@mui/icons-material/Assignment';

export default function ReferencePicker({ open, onClose, departmentId, attachedRefIds = [], onAttach }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    async function load() {
      let q;
      if (departmentId) {
        q = query(collection(db, 'referenceProjects'), where('departmentId', '==', departmentId));
      } else {
        q = collection(db, 'referenceProjects');
      }
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProjects(items);

      const pre = {};
      attachedRefIds.forEach(id => { pre[id] = true; });
      setSelected(pre);
      setLoading(false);
    }
    load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(id) {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function handleConfirm() {
    const result = projects
      .filter(p => selected[p.id])
      .map(p => ({ refId: p.id, name: p.name, client: p.client || '', role: p.role || '' }));
    onAttach(result);
    onClose();
  }

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderSpecialIcon sx={{ color: '#FF6B00' }} />
          <Typography variant="h6" fontWeight="bold">Välj referensuppdrag</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Inga referensuppdrag hittades för detta affärsområde.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Välj de referensuppdrag du vill inkludera i anbudet. Du kan sedan anpassa en variant per uppdrag.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {projects.map(p => (
              <Box key={p.id} sx={{
                display: 'flex', alignItems: 'center',
                py: 0.5, px: 1, borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}>
                <FormControlLabel
                  sx={{ flex: 1, m: 0 }}
                  control={
                    <Checkbox
                      checked={!!selected[p.id]}
                      onChange={() => toggle(p.id)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssignmentIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={selected[p.id] ? 600 : 400}>
                          {p.name}
                        </Typography>
                        {(p.client || p.role) && (
                          <Typography variant="caption" color="text.secondary">
                            {[p.client, p.role].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {selectedCount > 0 ? `${selectedCount} uppdrag valda` : 'Välj referensuppdrag'}
        </Typography>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Lägg till valda
        </Button>
      </DialogActions>
    </Dialog>
  );
}
